import { ACTION_TYPES, TURN_PHASES, COLUMN_HEIGHTS, COLUMNS_TO_WIN } from '../constants';
import { moveContext, bustProbability, nextStepIn } from '../moves';
import { claimedCount } from '../actions/turnActions';

// A heuristic opponent for Can't Stop.
//
// The game is two decisions, made over and over: WHICH pairing to play, and
// whether to ROLL AGAIN or bank what you have. The second one is the famous
// one, and it gets an exact answer rather than a folk rule: the engine can
// enumerate all 1296 rolls and compute the true bust probability for the
// current runner set (see bustProbability in ../moves.js), so the bot weighs
// real odds against real progress. Everything below is arithmetic on public
// information - the whole board is face-up in this game, so there is nothing
// to peek at and no restraint needed.

/**
 * How much of a claimed column one step is worth: a 3-space column pays a
 * third of a claim per step, the 13-space 7 about an eighth. Claims are the
 * only score in the game, so "value" is always measured in claims.
 */
const stepValue = (column) => 1 / COLUMN_HEIGHTS[column];

/**
 * The value at stake if the next roll busts: every step the runners have
 * gained this turn, plus a premium for each runner standing on a top space -
 * a bank now converts that runner into a whole claim, and a bust vaporises
 * it. The premium is most of a claim, not all of one, because an unclaimed
 * column can be re-climbed; a lost claim usually costs tempo, not the game.
 */
const progressAtRisk = (state) => {
  const me = state.players[state.meta.activePlayerIndex];
  let value = 0;
  for (const [columnKey, step] of Object.entries(state.turn.runners)) {
    const column = Number(columnKey);
    const from = me.markers[column] || 0;
    value += (step - from) * stepValue(column);
    if (step >= COLUMN_HEIGHTS[column]) value += 0.8;
  }
  return value;
};

/**
 * Roughly what one more successful roll is worth. Two advances is the common
 * case; where they land depends on how many runners are already out. With
 * runners to spare the roll can open fresh columns (average-ish step value);
 * with all three placed it can only climb the columns already underway.
 */
const expectedGain = (state) => {
  const runnerColumns = Object.keys(state.turn.runners).map(Number);
  if (runnerColumns.length < 3) return 2 * 0.14; // ~average step value across the board
  const average = runnerColumns.reduce((sum, c) => sum + stepValue(c), 0) / runnerColumns.length;
  return 2 * average;
};

/**
 * Bot temperaments. Deliberately deterministic - no RNG - so a seed
 * reproduces a whole game exactly. Variety comes from giving the seats
 * different personalities, not from dice.
 *
 * `nerve` scales how much expected gain it takes to keep rolling: above 1
 * keeps rolling on worse odds, below 1 banks earlier.
 *
 * TUNED against simulation (the no-thanks repo's lesson: check whether a
 * "style" axis is secretly a strength axis before shipping three of them).
 * Candidate-vs-two-steady, 300 seeded 3-player games per point:
 *
 *   nerve      0.5   0.72  0.85  1.0   1.2   1.45  1.8   2.5
 *   win rate   19%   28%   32%   33%   38%   28%   34%   26%
 *
 * The curve is FLAT from ~0.85 to ~1.8 (the wiggles are within noise at
 * N=300) with a real cliff only below ~0.7 - so unlike no-thanks's
 * `patience`, nerve genuinely is a style axis across the range used here,
 * with cautious taking a small deliberate strength haircut for the sake of
 * a bot that visibly banks early. The mixed table confirms it: 600 games of
 * cautious/steady/daring rotating seats came out 32.5% / 32.5% / 35.0% -
 * three temperaments, not three difficulty levels.
 */
// The labels are NAMES, not adjectives (bug report, Matt, 2026-08-29: "We
// need better names for the bots"). The day-one build shipped the sibling
// games' bare temperament labels - Cautious / Steady / Daring - which read
// as difficulty settings, not opponents. These keep the temperament legible
// (you should be able to guess who'll bank early and who'll ride a third
// runner into the 2 column) while being somebody you can lose to.
export const PERSONALITIES = {
  cautious: { nerve: 0.72, label: 'Careful Carl' },
  steady: { nerve: 1.0, label: 'Steady Betty' },
  daring: { nerve: 1.45, label: 'Reckless Rex' },
};

export const PERSONALITY_ORDER = ['steady', 'cautious', 'daring'];

/**
 * Score one offered play, for the pairing choice.
 *
 * Progress is the base: each advance is worth its column's step value, with
 * a bonus for landing on a top space (a claim waiting to be banked). From
 * that, subtract a fraction of the bust probability the play leaves behind -
 * the real cost of a placement is not the step, it's the rolls you now have
 * to survive. Scoring the AFTERMATH this way is what teaches the bot,
 * without a single special case, that a third runner on the 2 column is
 * usually a mistake and that doubling up on the 7 is fine.
 */
const scoreOption = (state, option) => {
  const ctx = moveContext(state);
  const runners = { ...ctx.runners };
  let value = 0;

  for (const sum of option.sums) {
    const landing = nextStepIn(sum, { ...ctx, runners });
    runners[sum] = landing;
    value += stepValue(sum);
    if (landing >= COLUMN_HEIGHTS[sum]) value += 0.5;
  }

  const after = bustProbability({ ...ctx, runners });
  return value - after * 0.35;
};

/**
 * The decision. Three turn phases, three answers:
 *
 *  - choosing: pick the highest-scoring play.
 *  - busted:   acknowledge and pass; nothing to decide.
 *  - rolling:  the push-your-luck call. Roll while the expected value of one
 *    more roll beats the expected loss - p(bust) x progress-at-risk against
 *    (1 - p) x gain, with `nerve` scaling the gain side. Two overrides, both
 *    absolute: a first roll is free (nothing at risk, and stopping isn't
 *    even legal), and a stop that WINS THE GAME is never passed up.
 */
export const decideAction = (state, personalityKey = 'steady') => {
  const { nerve } = PERSONALITIES[personalityKey] ?? PERSONALITIES.steady;
  const { phase } = state.turn;

  if (phase === TURN_PHASES.BUSTED) return { type: ACTION_TYPES.ACK_BUST, reason: 'busted' };

  if (phase === TURN_PHASES.CHOOSING) {
    let best = 0;
    let bestScore = -Infinity;
    state.turn.options.forEach((option, index) => {
      const score = scoreOption(state, option);
      if (score > bestScore) { bestScore = score; best = index; }
    });
    return { type: ACTION_TYPES.CHOOSE_MOVE, optionIndex: best, reason: 'best-play' };
  }

  // phase === rolling
  if (!state.turn.moved) return { type: ACTION_TYPES.ROLL, reason: 'first-roll' };

  const me = state.players[state.meta.activePlayerIndex];
  const pendingClaims = Object.entries(state.turn.runners)
    .filter(([column, step]) => step >= COLUMN_HEIGHTS[Number(column)]).length;
  if (claimedCount(state, me.id) + pendingClaims >= COLUMNS_TO_WIN) {
    return { type: ACTION_TYPES.STOP, reason: 'winning-stop' };
  }

  const p = bustProbability(moveContext(state));
  const keepRolling = (1 - p) * expectedGain(state) * nerve > p * progressAtRisk(state);
  return keepRolling
    ? { type: ACTION_TYPES.ROLL, reason: 'odds-good' }
    : { type: ACTION_TYPES.STOP, reason: 'banking' };
};

export const personalityFor = (seatIndex) => (
  PERSONALITY_ORDER[seatIndex % PERSONALITY_ORDER.length]
);
